/**
 * @korvo/shell - Reusable Next.js app shell
 * 
 * This package provides:
 * - Generic layout structure (customizable metadata/branding)
 * - Module routing infrastructure
 * - Admin framework
 * - Auth pages (signin, register, password reset)
 * - Error pages (404, error boundary)
 * - Generic routing patterns
 * - Default implementations for ErrorBoundary, SessionProvider, SessionTimeout
 * - ShellLayout for root layout — single shell, TopBar/footer hidden on /auth (import from @korvo/shell/server/layout)
 */

// Export shell components
export * from './components/auth/RegisterForm';
// SignInForm is not re-exported from the barrel — Turbopack fails (ModuleId) when the barrel is
// pulled into server graphs (e.g. generated-module-registry-loader) alongside next/dynamic in SignInForm.
// Import the component from `@korvo/shell/components/auth/SignInForm`; types stay on the barrel.
export type { SignInFormProps } from './components/auth/SignInForm.types';
export * from './components/ErrorPage';
export * from './components/GlobalErrorPage';
export * from './components/homepage/RegistrationCodeInput';
export * from './components/ModuleAdminPageLoader';
export * from './components/ModulePageLoader';
export * from './components/NotFoundPage';
export { useSlots } from './components/SlotsContext';
export { TopBarCenterContentProvider, useTopBarCenterContent } from './components/TopBarCenterContentContext';

// Export default implementations
export * from './components/defaults/DefaultErrorBoundary';
export * from './components/defaults/DefaultSessionProvider';
export * from './components/defaults/DefaultSessionTimeout';

// Export page components (client-safe only; admin pages use @korvo/shell/admin-server)
export { FULL_ADMIN_MENU_ITEMS,getShellAdminMenuItems } from './pages/admin/menu-items';
export * from './pages/dashboard-home-page';
export * from './pages/RegisterPage';
export * from './pages/SignInPage';

// Export admin components
export { AdminDashboardPage } from './components/admin/AdminDashboardPage';
export * from './components/admin/AdminSettingsClientLayout';
export { AdminSidebar } from './components/admin/AdminSidebar';

// Export shell configuration
export * from './config/shell-config';

// Note: Default auth helpers are NOT exported from the main index to avoid
// Next.js 15 "use server" conflicts. Import directly:
// import { GET, POST } from '@korvo/shell/auth/default-auth';

// Do NOT re-export @korvo/core/derivative-app here: it pulls prisma/pg into client bundles.
// Import auth-helpers etc. from '@korvo/core/derivative-app/auth-helpers' in server code.

